import { useState, useEffect } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { useTheme } from "../context/ThemeContext";

function CountdownBar({
  duration,
  color,
  trackColor,
}: {
  duration: number; // ms
  color: string;
  trackColor: string;
}) {
  return (
    <div
      style={{
        height: 3,
        background: trackColor,
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          height: "100%",
          background: color,
          width: "100%",
          animation: `pwaDrain ${duration}ms linear forwards`,
        }}
      />
    </div>
  );
}

// ── PwaUpdatePrompt ───────────────────────────────────────────────────────────
export function PwaUpdatePrompt() {
  const { theme, isDark } = useTheme();
  const [offlineReady, setOfflineReady] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const { needRefresh: [needRefresh], updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_swUrl, r) {
      if (!r) return;
      const poll = setInterval(() => r.update(), 10 * 60 * 1000);
      const onVisible = () => {
        if (document.visibilityState === "visible") r.update();
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => {
        clearInterval(poll);
        document.removeEventListener("visibilitychange", onVisible);
      };
    },
    onOfflineReady() {
      setOfflineReady(true);
      setTimeout(() => setOfflineReady(false), 4000);
    },
  });

  const handleUpdate = () => {
    setUpdating(true);
    updateServiceWorker(true);
  };

  useEffect(() => {
    if (!needRefresh) return;
    setCountdown(10);
    const id = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [needRefresh]);

  useEffect(() => {
    if (!needRefresh || countdown > 0) return;
    handleUpdate();
  }, [countdown, needRefresh]);

  const shell: React.CSSProperties = {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 99999,
    background: theme.bg.card,
    border: `1px solid ${theme.border.default}`,
    borderRadius: 14,
    padding: "12px 16px",
    boxShadow: isDark
      ? "0 12px 40px rgba(0,0,0,0.6)"
      : "0 12px 40px rgba(15,23,42,0.14)",
    animation: "pwaIn 0.3s cubic-bezier(0.16,1,0.3,1) both",
    maxWidth: 300,
  };

  return (
    <>
      <style>{`
        @keyframes pwaIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pwaDrain { from { width: 100%; } to { width: 0%; } }

        /* Responsive tweaks for small screens */
        @media (max-width: 480px) {
          .pwa-shell {
            left: 12px !important;
            right: 12px !important;
            bottom: 12px !important;
            max-width: none !important;
          }
          .pwa-install-row {
            flex-wrap: wrap;
          }
          .pwa-install-actions {
            width: 100%;
            justify-content: flex-end;
          }
        }
      `}</style>

      {needRefresh && (
        <div
          className="pwa-shell"
          style={{
            ...shell,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            overflow: "hidden",
            pointerEvents: "all",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 14,
              border: `1px solid ${theme.accent.gold}66`,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              position: "relative",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: theme.accent.gold,
                  fontFamily: theme.font.mono,
                }}
              >
                Update available
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: theme.text.muted,
                  fontFamily: theme.font.mono,
                  marginTop: 2,
                }}
              >
                {updating ? "Updating…" : `Auto-refreshing in ${countdown}s`}
              </div>
            </div>
            <button
              onClick={handleUpdate}
              disabled={updating}
              style={{
                background: `linear-gradient(135deg,${theme.accent.gold},#f59e0b)`,
                border: "none",
                borderRadius: 7,
                padding: "7px 14px",
                color: "#0f172a",
                fontFamily: theme.font.mono,
                fontSize: 11,
                fontWeight: 700,
                cursor: updating ? "not-allowed" : "pointer",
                opacity: updating ? 0.7 : 1,
                flexShrink: 0,
              }}
            >
              {updating ? "…" : `Update (${countdown}s)`}
            </button>
          </div>

          {!updating && (
            <div
              style={{
                height: 3,
                background: `${theme.accent.gold}26`,
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <div
                style={{
                  height: "100%",
                  background: theme.accent.gold,
                  width: `${(countdown / 10) * 100}%`,
                  transition: "width 1s linear",
                }}
              />
            </div>
          )}
        </div>
      )}

      {offlineReady && !needRefresh && (
        <div
          className="pwa-shell"
          style={{
            ...shell,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            pointerEvents: "none",
            border: `1px solid ${theme.accent.cyan}55`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: theme.accent.cyan,
                  fontFamily: theme.font.mono,
                }}
              >
                Ready offline
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: theme.text.muted,
                  fontFamily: theme.font.mono,
                }}
              >
                App cached for offline use
              </div>
            </div>
          </div>
          <CountdownBar
            duration={4000}
            color={theme.accent.cyan}
            trackColor={`${theme.accent.cyan}26`}
          />
        </div>
      )}
    </>
  );
}

const INSTALL_AUTO_DISMISS_MS = 15_000;

export function PwaInstallBanner() {
  const { theme, isDark } = useTheme();
  const [prompt, setPrompt] = useState<
    (Event & { prompt: () => Promise<void> }) | null
  >(null);
  const [dismissed, setDismissed] = useState(
    () => !!localStorage.getItem("pwa_install_dismissed")
  );
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setPrompt(e as Event & { prompt: () => Promise<void> });
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (!prompt || dismissed) return;
    setVisible(true);
    const id = setTimeout(() => setVisible(false), INSTALL_AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [prompt, dismissed]);

  if (!prompt || dismissed || !visible) return null;

  const install = async () => {
    await prompt.prompt();
    setPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem("pwa_install_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      className="pwa-shell"
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 99997,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: theme.bg.card,
        border: `1px solid ${theme.border.default}`,
        borderRadius: 14,
        padding: "14px 16px",
        boxShadow: isDark
          ? "0 8px 32px rgba(0,0,0,0.5)"
          : "0 8px 32px rgba(15,23,42,0.12)",
        animation: "pwaIn 0.35s ease both",
        maxWidth: 340,
        overflow: "hidden",
      }}
    >
      <div
        className="pwa-install-row"
        style={{ display: "flex", alignItems: "center", gap: 12 }}
      >
        <img
          src="/icon-192.png"
          alt="QASHUP"
          style={{
            width: 40,
            height: 40,
            objectFit: "contain",
            borderRadius: 10,
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: theme.text.primary,
              fontFamily: theme.font.mono,
            }}
          >
            Install QASHUP Owner
          </div>
          <div
            style={{
              fontSize: 11,
              color: theme.text.muted,
              fontFamily: theme.font.mono,
              marginTop: 2,
            }}
          >
            Quick access from your home screen
          </div>
        </div>

        <div
          className="pwa-install-actions"
          style={{ display: "flex", gap: 6, flexShrink: 0 }}
        >
          <button
            onClick={install}
            style={{
              background: `linear-gradient(135deg,${theme.accent.cyan},#0ea5e9)`,
              border: "none",
              borderRadius: 8,
              padding: "8px 14px",
              color: "#0f172a",
              fontFamily: theme.font.mono,
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              transition: "transform 0.15s ease, filter 0.15s ease",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = "brightness(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = "brightness(1)";
            }}
          >
            Install
          </button>

          <button
            onClick={dismiss}
            aria-label="Dismiss"
            style={{
              background: "transparent",
              border: `1px solid ${theme.border.default}`,
              borderRadius: 8,
              width: 32,
              height: 32,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.text.muted,
              cursor: "pointer",
              fontFamily: theme.font.mono,
              fontSize: 12,
              lineHeight: 1,
              flexShrink: 0,
              transition: "background 0.15s ease, color 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${theme.accent.cyan}1a`;
              e.currentTarget.style.color = theme.text.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = theme.text.muted;
            }}
          >
            ✕
          </button>
        </div>
      </div>

      <CountdownBar
        duration={INSTALL_AUTO_DISMISS_MS}
        color={theme.accent.cyan}
        trackColor={`${theme.accent.cyan}26`}
      />
    </div>
  );
}